import { ECreditApplicationStatus, EPaymentPlan } from '@if/core/types'
import {
  formatCurrencyNoDecimals,
  formatCurrencyNoDecimalsNoZero,
  formatIsoDate,
  getBooleanFromQueryString,
  getNumberFromQueryString,
  getStringArrayFromQueryString,
  getStringFromQueryString,
} from '@if/core/utils'
import { Box, Link, Stack, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { calculateDaysUntilExpiration } from '@src/services/Formatter'
import { CreditDashboardEntry } from '@src/types/CreditDashboardEntry'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getEndDate, getStartDate } from './browseCreditApplicationPage-selector'
import CreditApplicationProgressStatusBar from './components/CreditApplicationProgressStatusBar'
import MerchantNotificationIcons from './components/MerchantNotificationIcons'
import { ECreditApplicationStatusFilters } from './components/creditEntriesFiltersDialog-hooks'

export function useCreditDashboardEntriesColumns(
  isVeterinary: boolean,
  canSkipFlinks: boolean,
  hasDetailAccess: boolean,
  unreadCreditApplicationIds?: Set<string>,
) {
  const { t } = useTranslation()

  // Table def
  const columns = useMemo<GridColDef<CreditDashboardEntry>[]>(
    () => [
      {
        field: 'financingProgramId',
        headerName: t('applicationHeader.program'),
        description: t('applicationHeader.program'),
        flex: 0,
        minWidth: 80,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
          return (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'inline-block', whiteSpace: 'normal', verticalAlign: 'middle' }}
            >
              {t(`EFinancingProgram.${params.row.financingProgramId}`, { ns: 'enum' })}
            </Typography>
          )
        },
      },
      {
        field: 'creditApplicationReferenceNumber',
        headerName: t('applicationHeader.applicationNumber'),
        description: t('applicationHeader.applicationNumber'),
        flex: 0,
        minWidth: 80,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
          if (!hasDetailAccess) return <span>{params.row.creditApplicationReferenceNumber}</span>

          if (params.row.creditApplicationStatus === ECreditApplicationStatus.Draft)
            return (
              <Link href={`/credits/${params.row.financingProgramId}/${params.row.id}/edit`}>
                {params.row.creditApplicationReferenceNumber}
              </Link>
            )
          return (
            <Link href={`/credits/${params.row.financingProgramId}/${params.row.id}`}>
              {params.row.creditApplicationReferenceNumber}
            </Link>
          )
        },
      },
      {
        field: 'merchantName',
        headerName: t('applicationHeader.merchant'),
        description: t('applicationHeader.merchant'),
        flex: 2,
        minWidth: 100,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'clientName',
        headerName: t('applicationHeader.name'),
        description: t('applicationHeader.name'),
        flex: 2,
        minWidth: 150,
        headerAlign: 'left',
        align: 'left',
        display: 'flex',
        alignItems: 'center',
        renderCell: (params) => {
          const programConfig = programConfigByFinancingProgramId(params.row.financingProgramId)
          return (
            <Stack>
              <Typography variant="inherit">{params.row.clientName}</Typography>
              {params.row.beneficiaryName && programConfig.beneficiaryIsRequired && (
                <Typography variant="caption" color="text.secondary">
                  {isVeterinary
                    ? `${t('common.petName')}: ${params.row.beneficiaryName}`
                    : `${t('common.beneficiary')}: ${params.row.beneficiaryName}`}
                </Typography>
              )}
            </Stack>
          )
        },
      },
      {
        field: 'maxAmountFinanced',
        headerName: t('applicationHeader.approvedLimit'),
        description: t('applicationHeader.approvedLimit'),
        flex: 1,
        minWidth: 100,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
          const programConfig = programConfigByFinancingProgramId(params.row.financingProgramId)

          return programConfig.useMaxPayment ? formatCurrencyNoDecimals(params.value as string | number) : ''
        },
      },
      {
        field: 'interestRate',
        headerName: t('applicationHeader.rate'),
        description: t('applicationHeader.rate'),
        flex: 1,
        minWidth: 60,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
          let formattedValue = params.value !== undefined ? `${params.value}` : ''
          if (formattedValue === '0') {
            return ''
          }
          if (params.row.paymentPlanId !== EPaymentPlan.RegularDailyInterests) {
            return 'PROMO'
          }
          formattedValue += ' %'

          return formattedValue
        },
      },
      {
        field: 'amountRequested',
        headerName: `${t('applicationHeader.loan')}`,
        description: t('applicationHeader.loan'),
        flex: 1,
        minWidth: 100,
        headerAlign: 'center',
        align: 'center',
        valueFormatter: (value) => formatCurrencyNoDecimalsNoZero(value),
      },
      {
        field: 'status',
        headerName: t('applicationHeader.status'),
        description: t('applicationHeader.status'),
        flex: 1.75,
        minWidth: 250,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        renderCell: (params) => {
          const programConfig = programConfigByFinancingProgramId(params.row.financingProgramId)
          return (
            <Box sx={{ display: 'flex', alignItems: 'baseline«', height: '100%' }}>
              <CreditApplicationProgressStatusBar
                status={params.row.progressionStatus}
                prequalDecision={params.row.prequalificationDecision}
                hardHitDecision={params.row.hardHitDecision}
                incomeConfirmed={params.row.incomeConfirmed}
                isContractSignatureMethodSelected={params.row.isContractSignatureMethodSelected}
                creditApplicationStatus={params.row.creditApplicationStatus}
                currentCustomerStep={params.row.currentCustomerStep}
                missingRequiredDocuments={params.row.missingRequiredDocuments}
                originSystemId={params.row.originSystemId}
                canSkipFlinks={canSkipFlinks}
                displayPrequalify={programConfig.displayPrequalify}
                displayIncomeVerification={programConfig.displayIncomeVerification}
              />
            </Box>
          )
        },
      },
      {
        field: 'createdOn',
        headerName: t('applicationHeader.date'),
        description: t('applicationHeader.date'),
        flex: 1.25,
        minWidth: 175,
        headerAlign: 'left',
        align: 'left',
        display: 'flex',
        alignItems: 'center',
        renderCell: (params) => {
          const daysUntilExpiration = params.row.expiresOn ? calculateDaysUntilExpiration(params.row.expiresOn) : null
          const dayLabel = daysUntilExpiration !== null && daysUntilExpiration <= 1 ? t('common.day') : t('common.days')

          return (
            <Stack>
              {params.row.createdOn && (
                <Typography variant="inherit">
                  {t('applicationHeader.dateCreated')}: {formatIsoDate(params.row.createdOn)}
                </Typography>
              )}
              {daysUntilExpiration !== null && (
                <Typography variant="caption" color="text.secondary">
                  {t('applicationHeader.daysUntilExpiration', { count: daysUntilExpiration, unit: dayLabel })}
                </Typography>
              )}
            </Stack>
          )
        },
      },
      {
        field: 'merchantReferenceCode',
        headerName: t('applicationHeader.yourReferenceCode'),
        description: t('applicationHeader.yourReferenceCode'),
        flex: 2,
        minWidth: 100,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
          return (
            <Stack>
              {params.row.merchantReferenceCode && (
                <Typography variant="inherit">{params.row.merchantReferenceCode}</Typography>
              )}
            </Stack>
          )
        },
      },
      {
        field: 'Action',
        headerName: 'Action',
        description: 'Action',
        flex: 0,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterable: false,
        resizable: false,
        renderCell: (params) => {
          return (
            <div>
              <MerchantNotificationIcons
                merchantNotificationCodes={params.row.merchantNotificationCodes}
                requiredDocuments={params.row.missingRequiredDocuments}
                originSystemId={params.row.originSystemId}
                hasUnreadMessages={unreadCreditApplicationIds?.has(params.row.id) ?? false}
              />
            </div>
          )
        },
      },
    ],
    [t, isVeterinary, canSkipFlinks, hasDetailAccess, unreadCreditApplicationIds],
  )

  return columns
}

export function useCreditDashboardEntriesQuery(searchParams: URLSearchParams) {
  const query = useMemo(() => {
    const searchTerm = getStringFromQueryString(searchParams, 'searchTerm', '')
    return {
      limit: getNumberFromQueryString(searchParams, 'limit', 25),
      offset: getNumberFromQueryString(searchParams, 'offset', 0),
      financingProgramIds: getStringArrayFromQueryString(searchParams, 'financingProgramIds'),
      territoryIds: getStringArrayFromQueryString(searchParams, 'territoryIds'),
      creditApplicationStatus: getStringFromQueryString<ECreditApplicationStatusFilters>(
        searchParams,
        'creditApplicationStatus',
        ECreditApplicationStatus.Active,
      ),
      searchTerm: searchTerm,
      merchantId: getStringFromQueryString(searchParams, 'merchantId', ''),
      dateRange: getStringFromQueryString(searchParams, 'dateRange', ''),
      plan: getStringArrayFromQueryString(searchParams, 'plan'),
      progressionStatus: getStringArrayFromQueryString(searchParams, 'progressionStatus'),
      startDate: getStringFromQueryString(searchParams, 'startDate', getStartDate()),
      endDate: getStringFromQueryString(searchParams, 'endDate', getEndDate()),
      currentCustomerStep: getStringFromQueryString(searchParams, 'currentCustomerStep', ''),
      requiresMerchantAttention: getBooleanFromQueryString(searchParams, 'requiresMerchantAttention'),
      originSystemId: getStringFromQueryString(searchParams, 'originSystemId', ''),
      createdByUserId: getStringFromQueryString(searchParams, 'createdByUserId', ''),
    }
  }, [searchParams])

  return query
}
